import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import enLabels from "./labels.en.json";
import huLabels from "./labels.hu.json";
import { ADMIN_UI_LANGUAGE_STORAGE_KEY } from "./storageKeys";

export const SUPPORTED_UI_LANGUAGES = ["en", "hu"] as const;

export type UiLanguage = (typeof SUPPORTED_UI_LANGUAGES)[number];
export type TranslationKey = keyof typeof enLabels;

type TranslationDictionary = Record<TranslationKey, string>;

type I18nContextValue = {
  language: UiLanguage;
  setLanguage: (language: string) => void;
  t: (key: TranslationKey) => string;
};

const englishDictionary: TranslationDictionary = enLabels;
const hungarianDictionary: TranslationDictionary = huLabels;

const TRANSLATIONS: Record<UiLanguage, TranslationDictionary> = {
  en: englishDictionary,
  hu: hungarianDictionary,
};

const DEFAULT_UI_LANGUAGE: UiLanguage = "en";

const isSupportedUiLanguage = (value: string): value is UiLanguage => {
  return SUPPORTED_UI_LANGUAGES.includes(value as UiLanguage);
};

const resolveUiLanguage = (value: string): UiLanguage => {
  const normalized = value.slice(0, 2).toLowerCase();
  return isSupportedUiLanguage(normalized) ? normalized : DEFAULT_UI_LANGUAGE;
};

const getBrowserLanguage = (): UiLanguage => {
  const languagePrefix = window.navigator.language.slice(0, 2).toLowerCase();
  return isSupportedUiLanguage(languagePrefix) ? languagePrefix : DEFAULT_UI_LANGUAGE;
};

const getInitialUiLanguage = (storageKey: string): UiLanguage => {
  const savedLanguage = window.localStorage.getItem(storageKey) ?? "";
  if (isSupportedUiLanguage(savedLanguage)) {
    return savedLanguage;
  }
  return getBrowserLanguage();
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({
  children,
  storageKey = ADMIN_UI_LANGUAGE_STORAGE_KEY,
}: {
  children: ReactNode;
  storageKey?: string;
}) => {
  const [requestedLanguage, setRequestedLanguage] = useState<string>(() => getInitialUiLanguage(storageKey));
  const language = useMemo<UiLanguage>(() => resolveUiLanguage(requestedLanguage), [requestedLanguage]);

  useEffect(() => {
    window.localStorage.setItem(storageKey, language);
  }, [language, storageKey]);

  const value = useMemo<I18nContextValue>(() => {
    return {
      language,
      setLanguage: setRequestedLanguage,
      t: (key: TranslationKey) => {
        const selected = TRANSLATIONS[language][key];
        if (selected) {
          return selected;
        }
        return TRANSLATIONS[DEFAULT_UI_LANGUAGE][key] ?? String(key);
      },
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
};
